import { useState, Fragment, ReactNode } from 'react';
import {
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    ListSubheader,
    ListItemAvatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { SttPagination, SttNoResults } from '../all';
import ModalConfirmation from '../../layout/ModalConfirmation';

type ChipItem = {
    caption: string;
};

type ListDataItem<T = unknown> = {
    caption: ReactNode;
    text?: string;
    avatar?: ReactNode;
    chips?: ChipItem[];
    objToSelect?: T;
    selected?: boolean;
};

type SttListProps<T = unknown> = {
    title?: string;
    paper?: boolean;
    data?: ListDataItem<T>[];
    onSelect?: (obj: T) => void;
    onDelete?: (obj: T) => void;
    confirmationDelete?: boolean;
    numRows?: number;
    onAdd?: () => void;
};

/**
 * List component
 * -------------
 *
 * Props
 *
 *     * string title
 *     * array data: components of each data element:
 *
 *        - string|Component caption: title
 *        - string text: secondary text
 *        - <Avatar> avatar
 *        - Chip[] chips: listado de duplas del tipo {caption}
 *        - Object objToSelect: en caso de que se use "onSelect", esta función
 *            devuelve este objeto.
 *        - boolean selected
 *
 *    * integer numRows: number of result to show. If row count is greater than
 *        this number then a pagination coomponent shows up
 *     * function onSelect(obj): si se establece se podrá seleccionar un elemento de la lista.
 *    * function onAdd: if it exists add adding button at the top-right corner
 *    * function onDelete(obj)
 *    * boolean paper: wrap it on paper
 */
export function SttList<T = unknown>({
    title,
    paper = false,
    data = [],
    onSelect,
    onDelete,
    confirmationDelete = false,
    numRows,
    onAdd,
}: SttListProps<T>) {
    const [page, setPage] = useState(1);
    const [confirmationDialog, setConfirmationDialog] = useState<{
        show: boolean;
        item?: ListDataItem<T>;
    }>({
        show: false,
    });

    const renderChips = (listItem: ListDataItem<T>) =>
        listItem.chips?.map((chip: any, idx: any) => (
            <span
                key={idx}
                style={{
                    padding: 4,
                    fontSize: 10,
                    backgroundColor: '#aaa',
                    color: 'white',
                    marginRight: 4,
                    marginTop: 4,
                }}
            >
                {chip.caption}
            </span>
        ));

    const handleOnSelect = (item: ListDataItem<T>) => {
        if (onSelect && item.objToSelect) {
            onSelect(item.objToSelect);
        }
    };

    const handleOnDelete = (item: ListDataItem<T>) => {
        if (onDelete && item.objToSelect) {
            onDelete(item.objToSelect);
        }
    };

    const pagination = (
        <SttPagination
            page={page}
            pages={Math.ceil(data.length / (numRows || 1))}
            count={data.length}
            onChangePage={setPage}
        />
    );

    const getPaginatedData = () => {
        if (numRows) {
            const idxIni = (page - 1) * numRows;
            const idxEnd = page * numRows;

            return data.filter((_e, i) => i >= idxIni && i < idxEnd);
        }

        return data;
    };

    const content = (
        <List
            subheader={
                <ListSubheader component="div">
                    {title}

                    {onAdd && (
                        <IconButton
                            style={{
                                position: 'absolute',
                                right: 5,
                            }}
                            onClick={onAdd}
                        >
                            <AddIcon />
                        </IconButton>
                    )}
                </ListSubheader>
            }
        >
            {numRows && data.length > numRows ? pagination : null}

            {data &&
                getPaginatedData().map((item: any, i: any) => {
                    const isClickable =
                        onSelect !== null && onSelect !== undefined;
                    const itemContent = (
                        <>
                            {item.avatar && (
                                <ListItemAvatar>{item.avatar}</ListItemAvatar>
                            )}

                            <ListItemText
                                primary={item.caption}
                                secondary={
                                    <>
                                        {item.text}
                                        {renderChips(item)}
                                    </>
                                }
                                sx={onDelete ? { pr: 5 } : undefined}
                            />

                            {onDelete && onDelete.constructor === Function && (
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (confirmationDelete) {
                                                setConfirmationDialog({
                                                    show: true,
                                                    item,
                                                });
                                            } else {
                                                handleOnDelete(item);
                                            }
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            )}
                        </>
                    );

                    return (
                        <Fragment key={i}>
                            <Divider />
                            {isClickable ? (
                                <ListItemButton
                                    style={{
                                        alignItems: 'flex-start',
                                        backgroundColor: item.selected
                                            ? '#faffe9'
                                            : undefined,
                                    }}
                                    onClick={() => handleOnSelect(item)}
                                >
                                    {itemContent}
                                </ListItemButton>
                            ) : (
                                <ListItem
                                    style={{
                                        alignItems: 'flex-start',
                                        backgroundColor: item.selected
                                            ? '#faffe9'
                                            : undefined,
                                    }}
                                >
                                    {itemContent}
                                </ListItem>
                            )}
                        </Fragment>
                    );
                })}

            {((data && data.length === 0) || !data) && (
                <ListItem>
                    <SttNoResults />
                </ListItem>
            )}
        </List>
    );

    const confirmationModal = (
        <ModalConfirmation
            show={confirmationDialog.show}
            onClose={() => setConfirmationDialog({ show: false })}
            onAccept={() => {
                if (confirmationDialog.item) {
                    handleOnDelete(confirmationDialog.item);
                }
                setConfirmationDialog({ show: false });
            }}
        />
    );

    // Render
    if (paper) {
        return (
            <Paper>
                {content}
                {confirmationModal}
            </Paper>
        );
    }

    return (
        <>
            {content}
            {confirmationModal}
        </>
    );
}
